import { useEffect } from 'react';

/**
 * useEscapeClose — modal/popover 의 ESC 닫기 표준 hook (modal hook trio 의 3번째).
 *
 * useModalLeave (motion) + useFocusTrap (a11y) + useEscapeClose (interaction) 의
 * 페어 trio. 5 modal 의 동일 useEffect + window keydown 등록 패턴 (각 ~5 LOC ×
 * 5 = 25 LOC 중복) 을 단일 hook 호출로 통합.
 *
 * 사용:
 *   useEscapeClose(onClose);                  // 항상 활성
 *   useEscapeClose(onClose, submitting);       // submitting 중에는 ESC 차단
 *
 * disabled=true 시:
 *   ESC 무시. 5 modal 중 TaskCreateModal / FolderCreateModal 의 *submitting
 *   가드* 패턴 호환 — submitting 동안 사용자가 ESC 로 close 하면 inflight 요청 +
 *   modal unmount race 방지.
 *
 * 활성 시점:
 *   modal 이 mount 된 동안만 hook 활성 (useEffect 의 자연 lifecycle). useModalLeave
 *   의 shouldRender 가 false → modal unmount → hook cleanup → keydown listener 해제.
 *   leaving phase (shouldRender=true + isLeaving=true) 동안에는 hook 활성 — ESC
 *   재호출 가능 (idempotent close — onClose 가 이미 setOpen(false) 호출 후라 재
 *   호출은 race 없음).
 *
 * window vs container:
 *   window 등록 — focus 가 modal 안 element 에 있든 modal 외부 (예: leaving phase
 *   에서 trigger 복귀 직전) 든 ESC 처리. ComposeModal 의 기존 div onKeyDown 패턴
 *   (focus 가 modal 안일 때만 fire) 보다 robust.
 *
 * 관련:
 *   adr-20260506-focus-trap-hook-without-native-dialog (focus trap 짝)
 *   pattern-modal-leave-hook-latched-position-tree (motion 짝)
 */

export function useEscapeClose(onClose: () => void, disabled = false): void {
  useEffect(() => {
    if (disabled) return;
    function onKey(e: KeyboardEvent) {
      // ★ 2026-09-15 — 안쪽 목록 · 메뉴(Combobox · Select · DropdownMenu)가 Esc 를 먼저 처리했으면
      //   (preventDefault) 그 Esc 는 «목록 닫기» 다 — 모달까지 닫지 않는다. 겹친 모달 · 앵커 메뉴는 범위 밖(레이어 스택 안 B).
      if (e.key === 'Escape' && !e.defaultPrevented) onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, disabled]);
}
